import { useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutGroup } from 'framer-motion'
import { xor } from 'lodash-es'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import {
  getCarcLabelByCode,
  getNateraTagLabelByCode,
  getRarcLabelByCode,
} from '~/entities/carc-rarc'
import {
  getCategoryMetadata,
  isAppealDeniedUpheldAutomated,
  isAppealDeniedUpheldOutOfNetwork,
  isClaimAcknowledged,
  isClaimAppealAcknowledgement,
  isClaimAppealDeniedUpheldMedicalNecessity,
  isClaimAppealDeniedUpheldNoPriorAuthorization,
  useSubcategories,
} from '~/entities/categories'
import {
  DATA_EXTRACTION_FIELDS_CONFIG,
  DOCUMENT_SERVER_STATUS,
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getIsDataExtractionFinished,
  isTaggingClassification,
  PRIOR_AUTH_PHASES,
  ProcessingAlert,
  useClassificationReviews,
  useCurrentDocument,
  useDocumentExtractedCarcRarcData,
  useDocumentExtractedDenialData,
  useDocumentProcessingPolling,
  useUpdateDocumentPayor,
  type DataExtractionFieldName,
} from '~/entities/document'
import { FLAG_TYPES, useStepFlagState } from '~/entities/document-flags'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { useFirstInteraction, useStepCompletion, useStepView } from '~/features/document-analytics'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { normalizeDateToYyyyMmDd } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useAppForm } from '~/shared/lib/form'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import { Alert, IconButton } from '~/shared/ui'
import { useIsPdfCollapsed } from '~/widgets/document-review-layout'
import { ReviewActions } from '~/widgets/review-actions'
import { useAddExtractedCarcRarcDataReview, useAddExtractedDenialDataReview } from '../../api'
import { CONFIDENCE_SCORE_THRESHOLD, GROUPS } from '../../config'
import type { DataExtractionMode } from '../types'
import { FieldsGroup } from './fields-group'
import { FieldsSubgroup } from './fields-subgroup'
import { FormFields } from './form-fields/form-fields'

export interface DataExtractionRef {
  applyChanges: () => void
  cancelChanges: () => void
}

export const DataExtraction = forwardRef<DataExtractionRef, { mode: DataExtractionMode }>(
  function DataExtraction({ mode }, ref) {
    useStepView()
    const formRef = useRef<HTMLFormElement>(null)
    useFirstInteraction(formRef)
    const { trackStepCompletion } = useStepCompletion()
    const navigate = useNavigate()
    const {
      data: { document },
    } = useCurrentDocument()
    useDocumentProcessingPolling()
    const { data: extractedDenialData } = useDocumentExtractedDenialData({
      documentKey: document.key,
    })
    const { data: extractedCarcRarcData } = useDocumentExtractedCarcRarcData({
      documentKey: document.key,
    })
    const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')
    const {
      data: { items: classificationReviews },
    } = useClassificationReviews({ documentKey: document.key })
    const isFullyReviewed =
      document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
      document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
    const wasReclassified =
      isReviewStateResetEnabled && (classificationReviews?.length ?? 0) >= 2 && !isFullyReviewed
    const { mutateAsync: addExtractedDenialDataReview, isError: isDenialDataReviewError } =
      useAddExtractedDenialDataReview()
    const { mutateAsync: addExtractedCarcRarcDataReview, isError: isCarcRarcDataReviewError } =
      useAddExtractedCarcRarcDataReview()
    const { mutateAsync: updateDocumentPayor } = useUpdateDocumentPayor()
    const isPeerToPeerEnabled = useIsFeatureEnabled('2026-05-peer-to-peer')
    const isAppealAckDatesEnabled = useIsFeatureEnabled('2026-06-appeal-ack-dates')
    const isHidePaPayorP2pEnabled = useIsFeatureEnabled('2026-08-hide-pa-payor-p2p')
    const subcategories = useSubcategories()
    const shouldHidePaFields =
      isHidePaPayorP2pEnabled &&
      PRIOR_AUTH_PHASES.includes(
        getCategoryMetadata({ category: document.category, subcategories })?.phase?.label ?? '',
      )
    // Feature-flag gate: exclude flagged fields entirely when their flag is off so they are
    // not seeded into active keys, rendered, or included in save payloads.
    const denialFields = useMemo(() => {
      let fields = extractedDenialData?.fields ?? []
      if (!isPeerToPeerEnabled || shouldHidePaFields) {
        fields = fields.filter((field) => field.name !== 'peer_to_peer_indicator')
      }
      if (!isAppealAckDatesEnabled) {
        fields = fields.filter(
          (field) =>
            field.name !== 'appeal_date_received' && field.name !== 'notification_date_provided',
        )
      }
      return fields
    }, [
      extractedDenialData?.fields,
      isPeerToPeerEnabled,
      isAppealAckDatesEnabled,
      shouldHidePaFields,
    ])
    const noFieldsPresent = denialFields.length === 0
    const [isWarningAlertHiddenManually, setIsWarningAlertHiddenManually] = useState(false)
    const queryClient = useQueryClient()
    const isTaggingEnabled = isTaggingClassification(document.category, subcategories)

    const hasBeenReclassified =
      isReviewStateResetEnabled && (classificationReviews?.length ?? 0) >= 2
    const categoryMetadata = getCategoryMetadata({
      category: document.category,
      subcategories,
    })
    const isOonMatchingEntryEnabled = useIsFeatureEnabled(
      '2026-10-appeal-denied-upheld-oon-matching-entry',
    )
    // Categories whose entry point is Matching rather than Tagging (see
    // `getDocumentStep()`), so Validate hands off straight to patient matching.
    const skipsTagging =
      isClaimAppealAcknowledgement(document.category) ||
      isClaimAcknowledged(document.category) ||
      isAppealDeniedUpheldAutomated(document.category) ||
      (isOonMatchingEntryEnabled && isAppealDeniedUpheldOutOfNetwork(document.category))
    const shouldWaitForTags =
      isReviewStateResetEnabled &&
      isTaggingEnabled &&
      (!skipsTagging || hasBeenReclassified) &&
      (!isClaimAppealDeniedUpheldMedicalNecessity(categoryMetadata) || hasBeenReclassified) &&
      (!isClaimAppealDeniedUpheldNoPriorAuthorization(categoryMetadata) || hasBeenReclassified)
    const [tagsTimedOut, setTagsTimedOut] = useState(false)
    const { data: tagsData } = useQuery({
      ...apiClient.getDocumentTagsDocumentsKeyTagsGetOptions({
        path: { key: document.key },
      }),
      enabled: shouldWaitForTags && !tagsTimedOut,
      refetchInterval: (query) => {
        const items = query.state.data?.items ?? []
        const hasData = items.some((item) => item.original_fields_from_llm != null)
        return hasData ? false : 3000
      },
    })
    const tagsReady =
      !shouldWaitForTags ||
      tagsTimedOut ||
      (tagsData?.items ?? []).some((item) => item.original_fields_from_llm != null)

    useEffect(() => {
      if (!shouldWaitForTags || tagsReady) return
      const timer = setTimeout(() => setTagsTimedOut(true), 25_000)
      return () => clearTimeout(timer)
    }, [shouldWaitForTags, tagsReady])

    const queryParams = useAuditTrailQueryParams()
    const isPdfCollapsed = useIsPdfCollapsed()
    const [viewModeActiveFieldKeys, setViewModeActiveFieldKeys] = useState<string[]>([])
    const [editModeActiveFieldKeys, setEditModeActiveFieldKeys] = useState<string[]>([])
    const dataExtractionContainerRef = useRef<HTMLDivElement>(null)
    const stepFlag = useStepFlagState(FLAG_TYPES.STEP_DATA)

    const activeFieldKeys = useMemo(() => {
      return mode === 'view' ? viewModeActiveFieldKeys : editModeActiveFieldKeys
    }, [mode, viewModeActiveFieldKeys, editModeActiveFieldKeys])

    const toggleFieldActive = useCallback((fieldName: string) => {
      setEditModeActiveFieldKeys((prev) => {
        return xor(prev, [fieldName])
      })
    }, [])

    useEffect(() => {
      if (extractedDenialData?.fields) {
        const initialActiveFieldKeys = [
          // `peer_to_peer_indicator` must always be in the left column regardless of
          // the backend `required` / `reviewed` flags (see spec 023).
          ...denialFields
            .filter(
              (field) =>
                !!(field.required || field.reviewed) ||
                field.name === 'peer_to_peer_indicator' ||
                field.name === 'auth_date_range_from' ||
                field.name === 'auth_date_range_to' ||
                field.name === 'appeal_id' ||
                field.name === 'reference_number' ||
                (isAppealAckDatesEnabled &&
                  (field.name === 'appeal_date_received' ||
                    field.name === 'notification_date_provided')),
            )
            .map((field) => field.name),
          ...(isReviewStateResetEnabled && isTaggingEnabled
            ? []
            : ['carc_codes', 'rarc_codes', 'natera_tags', 'natera_tags_notes']),
          ...(shouldHidePaFields ? [] : ['payor_id']),
        ]
        setViewModeActiveFieldKeys(initialActiveFieldKeys)
        setEditModeActiveFieldKeys(initialActiveFieldKeys)
      }
    }, [
      extractedDenialData?.fields,
      denialFields,
      isAppealAckDatesEnabled,
      isReviewStateResetEnabled,
      isTaggingEnabled,
      shouldHidePaFields,
    ])

    // Sync temp state when entering edit mode
    useEffect(() => {
      if (mode === 'edit') {
        setEditModeActiveFieldKeys(viewModeActiveFieldKeys)
      }
    }, [mode, viewModeActiveFieldKeys])

    // Apply or cancel changes
    const applyChanges = useCallback(() => {
      setViewModeActiveFieldKeys(editModeActiveFieldKeys)
    }, [editModeActiveFieldKeys])

    const cancelChanges = useCallback(() => {
      setEditModeActiveFieldKeys(viewModeActiveFieldKeys)
    }, [viewModeActiveFieldKeys])

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      applyChanges,
      cancelChanges,
    }))

    const fieldsMap = useMemo(() => {
      return denialFields.reduce<
        Record<
          string,
          | {
              value: string
              confidence: number
              recommendedValue?: string
              required: boolean
            }
          | undefined
        >
      >(
        (acc, field) => {
          const fieldConfig = DATA_EXTRACTION_FIELDS_CONFIG[field.name as DataExtractionFieldName]
          let value = field.value as string
          if (
            fieldConfig &&
            fieldConfig.type === 'select' &&
            'defaultValue' in fieldConfig &&
            !fieldConfig.options.some((option) => option.value === value)
          ) {
            value = fieldConfig.defaultValue
          }
          if (fieldConfig && fieldConfig.type === 'date-input' && value) {
            value = normalizeDateToYyyyMmDd(value)
          }
          acc[field.name] = {
            value,
            confidence: field.confidence_score ?? 0,
            required: !!field.required,
          }
          return acc
        },
        {
          carc_codes: {
            value:
              (
                (extractedCarcRarcData?.fields.find((field) => field.name === 'carc_codes')
                  ?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            confidence: 1,
            recommendedValue:
              (
                ((extractedCarcRarcData?.original_fields_from_llm ?? []).find(
                  (field) => field.name === 'carc_codes',
                )?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            required: true,
          },
          rarc_codes: {
            value:
              (
                (extractedCarcRarcData?.fields.find((field) => field.name === 'rarc_codes')
                  ?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            confidence: 1,
            recommendedValue:
              (
                ((extractedCarcRarcData?.original_fields_from_llm ?? []).find(
                  (field) => field.name === 'rarc_codes',
                )?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            required: true,
          },
          natera_tags: {
            value:
              (
                (extractedCarcRarcData?.fields.find((field) => field.name === 'natera_tags')
                  ?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            confidence: 1,
            recommendedValue:
              (
                ((extractedCarcRarcData?.original_fields_from_llm ?? []).find(
                  (field) => field.name === 'natera_tags',
                )?.value || []) as apiClient.CarcRarcCode[]
              )
                .map((code) => code.code)
                .join(',') ?? '',
            required: true,
          },
          ...(shouldHidePaFields
            ? {}
            : {
                payor_id: {
                  value: document.payor_id ?? '',
                  confidence: 1,
                  required: true,
                },
              }),
          natera_tags_notes: {
            value: '',
            confidence: 1,
            required: true,
          },
        },
      )
    }, [
      denialFields,
      extractedCarcRarcData?.fields,
      extractedCarcRarcData?.original_fields_from_llm,
      document.payor_id,
      shouldHidePaFields,
    ])

    // Track if natera_tags have changed to determine if feedback is mandatory
    const getShouldShowMandatoryComment = useCallback(
      (
        carcCodesValue: string | undefined,
        rarcCodesValue: string | undefined,
        nateraTagsValue: string | undefined,
      ) => {
        const currentCarcCodes = carcCodesValue?.split(',').filter(Boolean).sort().join(',') || ''
        const recommendedCarcCodes =
          fieldsMap.carc_codes?.value?.split(',').filter(Boolean).sort().join(',') || ''
        const carcCodesChanged = currentCarcCodes !== recommendedCarcCodes

        const currentRarcCodes = rarcCodesValue?.split(',').filter(Boolean).sort().join(',') || ''
        const recommendedRarcCodes =
          fieldsMap.rarc_codes?.value?.split(',').filter(Boolean).sort().join(',') || ''
        const rarcCodesChanged = currentRarcCodes !== recommendedRarcCodes

        const currentTags = nateraTagsValue?.split(',').filter(Boolean).sort().join(',') || ''
        const recommendedTags =
          fieldsMap.natera_tags?.value?.split(',').filter(Boolean).sort().join(',') || ''
        const nateraTagsChanged = currentTags !== recommendedTags

        return nateraTagsChanged || carcCodesChanged || rarcCodesChanged
      },
      [fieldsMap.carc_codes?.value, fieldsMap.rarc_codes?.value, fieldsMap.natera_tags?.value],
    )

    const defaultValues = useMemo(() => {
      const taggingKeys = ['carc_codes', 'rarc_codes', 'natera_tags', 'natera_tags_notes']
      const shouldExcludeTaggingKeys = isReviewStateResetEnabled && isTaggingEnabled
      return Object.keys(fieldsMap)
        .filter((key) => key !== 'natera_tags_notes')
        .filter((key) => !shouldExcludeTaggingKeys || !taggingKeys.includes(key))
        .reduce<Record<DataExtractionFieldName, string | undefined>>(
          (acc, key) => {
            acc[key as DataExtractionFieldName] = fieldsMap[key]?.value ?? undefined
            return acc
          },
          {} as Record<DataExtractionFieldName, string | undefined>,
        )
    }, [fieldsMap, isReviewStateResetEnabled, isTaggingEnabled])

    const groups = useMemo(() => {
      return GROUPS.filter((group) => {
        // Filter out Tagging group when dedicated tagging step is enabled
        if (group.title === 'Tagging' && isTaggingEnabled) {
          return false
        }

        if (!document.category) {
          return true
        }

        const phase = getCategoryMetadata({ category: document.category, subcategories })?.phase
        if (!phase) {
          return false
        }

        // Filter out groups that don't match the document category
        const isIncluded =
          !group.phases || group.phases.length === 0 || group.phases.includes(phase.label)
        const isExcluded = group.excludePhases?.includes(phase.label)
        return isIncluded && !isExcluded
      })
        .map((group) => {
          return {
            ...group,
            subgroups: group.subgroups
              // Filter out fields that are not present in the API response
              .map((subgroup) => {
                return {
                  ...subgroup,
                  fields: subgroup.fields
                    .filter((field) => fieldsMap[field.key])
                    // Defensively gate the peer_to_peer_indicator field behind its flag.
                    .filter(
                      (field) =>
                        (isPeerToPeerEnabled && !shouldHidePaFields) ||
                        field.key !== 'peer_to_peer_indicator',
                    ),
                }
              })
              .filter((subgroup) => subgroup.fields.length > 0),
          }
        })
        .filter((group) => group.subgroups.length > 0)
    }, [
      document.category,
      subcategories,
      fieldsMap,
      isTaggingEnabled,
      isPeerToPeerEnabled,
      shouldHidePaFields,
    ])

    const isConfidenceLow = groups.some((group) =>
      group.subgroups.some((subgroup) =>
        subgroup.fields.some(
          (field) => (fieldsMap[field.key]?.confidence || 0) < CONFIDENCE_SCORE_THRESHOLD,
        ),
      ),
    )

    const form = useAppForm({
      defaultValues,
      validators: {
        onChange: ({ value }) => {
          if (isReviewStateResetEnabled && isTaggingEnabled) return undefined
          const nateraTagsChanged = getShouldShowMandatoryComment(
            value.carc_codes,
            value.rarc_codes,
            value.natera_tags,
          )
          if (nateraTagsChanged && !value.natera_tags_notes?.trim()) {
            return {
              fields: {
                natera_tags_notes: 'Please explain why you modified the Natera Tags.',
              },
            }
          }
          return undefined
        },
      },
      onSubmit: async ({ value }) => {
        const shouldFinishReview = noFieldsPresent && !!extractedDenialData?.key

        let extractedDenialFields
        if (shouldFinishReview) {
          extractedDenialFields = { review_status: 'reviewed' }
        }
        if (!noFieldsPresent) {
          extractedDenialFields = Object.fromEntries(
            Object.entries(value)
              .filter(
                ([key]) =>
                  ![
                    'carc_codes',
                    'rarc_codes',
                    'natera_tags',
                    'payor_id',
                    'natera_tags_notes',
                  ].includes(key),
              )
              .filter(([key]) => {
                if (activeFieldKeys.includes(key)) {
                  return true
                }
                // Always include select-type fields with a default value so their
                // value (including the default) is persisted on every save.
                const fieldConfig = DATA_EXTRACTION_FIELDS_CONFIG[key as DataExtractionFieldName]
                return (
                  !!fieldConfig &&
                  fieldConfig.type === 'select' &&
                  'defaultValue' in fieldConfig &&
                  !!fieldsMap[key]
                )
              })
              .map(([key, value]) => [key, value ?? null]),
          )
        }

        if (extractedDenialFields) {
          await addExtractedDenialDataReview({
            path: {
              key: document.key,
            },
            body: {
              doc_extracted_data_key: extractedDenialData?.key,
              is_valid: true,
              fields: extractedDenialFields,
            },
          })
        }

        const shouldUpdateCarcRarcData =
          !noFieldsPresent && !!extractedCarcRarcData?.key && !isTaggingEnabled
        if (shouldUpdateCarcRarcData) {
          const extractedCarcRarcFields = {
            carc_codes:
              value.carc_codes?.split(',').map((code) => ({
                type: 'carc' as const,
                code,
                description: getCarcLabelByCode(code) ?? '',
              })) ?? [],
            rarc_codes:
              value.rarc_codes?.split(',').map((code) => ({
                type: 'rarc' as const,
                code,
                description: getRarcLabelByCode(code) ?? '',
              })) ?? [],
            natera_tags:
              value.natera_tags?.split(',').map((code) => ({
                type: 'natera_tags' as const,
                code,
                description: getNateraTagLabelByCode(code) ?? '',
              })) ?? [],
          }
          await addExtractedCarcRarcDataReview({
            path: {
              key: document.key,
            },
            body: {
              data_key: extractedCarcRarcData?.key,
              is_valid: true,
              fields: extractedCarcRarcFields,
              ...(value.natera_tags_notes?.trim() && { notes: value.natera_tags_notes.trim() }),
            },
          })
        }

        const payorId = value.payor_id
        const shouldUpdatePayorId =
          !noFieldsPresent && document.payor_id !== value.payor_id && payorId
        if (shouldUpdatePayorId) {
          await updateDocumentPayor({
            path: { key: document.key },
            body: {
              payor_id: payorId,
            },
          })
        }

        trackStepCompletion()

        if (noFieldsPresent) {
          navigate(getDocumentsUrlWithCurrentParams())
        } else {
          queryClient.invalidateQueries(
            apiClient.getDocumentExtractedDataDocumentsKeyExtractedDataGetOptions({
              path: { key: document.key },
            }),
          )
          if (isTaggingEnabled && !skipsTagging) {
            const currentSearchParams = new URLSearchParams(window.location.search)
            if (queryParams) {
              Object.entries(queryParams).forEach(([key, value]) => {
                currentSearchParams.set(key, value)
              })
            }
            const queryString = currentSearchParams.toString()
              ? `?${currentSearchParams.toString()}`
              : ''
            navigate(`/documents/${document.key}/tagging${queryString}`)
          } else {
            navigate(getDocumentStepUrl(document.key, 'patient', { queryParams }))
          }
        }
      },
    })

    const defaultValuesRef = useRef(defaultValues)
    useEffect(() => {
      if (!isReviewStateResetEnabled) return
      if (defaultValuesRef.current !== defaultValues) {
        defaultValuesRef.current = defaultValues
        form.reset(defaultValues)
      }
    }, [defaultValues, form, isReviewStateResetEnabled])

    const {
      set: setHistoryValues,
      reset: resetHistoryValues,
      undo,
      redo,
      canUndo,
      canRedo,
    } = useUndoRedo({
      initialValues: defaultValues,
      setFieldValue: form.setFieldValue,
    })

    const { isScrolled, sentinelElement } = useIsScrolled()

    return (
      <LayoutGroup>
        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          {isConfidenceLow && !isWarningAlertHiddenManually && (
            <Alert
              className="mt-4"
              appearance="WARNING"
              materialIcon="warning"
              action={
                <IconButton
                  className="text-alert-warning-message"
                  size="medium"
                  container="small"
                  materialIcon="close"
                  onClick={() => setIsWarningAlertHiddenManually(true)}
                />
              }
            >
              opti flagged fields with low confidence. Please review them carefully before
              completing the step.
            </Alert>
          )}
          {document.review_status === DOCUMENT_SERVER_STATUS.processing && (
            <ProcessingAlert className={cn({ 'mb-2': !noFieldsPresent })} />
          )}
          {noFieldsPresent ? (
            <Alert
              className="mb-2"
              appearance="INFO"
              materialIcon="info"
              caption="All clear — no data to validate"
            >
              Looks like there’s nothing here to review.
            </Alert>
          ) : (
            <div
              key={isPdfCollapsed ? 'collapsed' : 'expanded'}
              ref={dataExtractionContainerRef}
              className="flex flex-col gap-5 pb-3"
            >
              {groups.map((group) => {
                // Don't render groups that don't have at least one active field in view mode
                if (
                  mode === 'view' &&
                  group.subgroups.every((subgroup) =>
                    subgroup.fields.every((field) => !activeFieldKeys.includes(field.key)),
                  )
                ) {
                  return null
                }

                return (
                  <FieldsGroup
                    key={group.title}
                    title={group.title}
                    main={!!group.main}
                  >
                    {group.subgroups.map((subgroup) => {
                      // Don't render subgroups that don't have at least one field coming from the API response
                      if (subgroup.fields.every((field) => !fieldsMap[field.key])) {
                        return null
                      }

                      // Don't render subgroups that are hidden by design
                      if (subgroup.hidden) {
                        return null
                      }

                      // Don't render mandatory comment field if Natera Tags or Carcs not changed, and in edit mode
                      if (
                        subgroup.fields.some((field) => field.key === 'natera_tags_notes') &&
                        (!getShouldShowMandatoryComment(
                          form.state.values.carc_codes,
                          form.state.values.rarc_codes,
                          form.state.values.natera_tags,
                        ) ||
                          mode === 'edit')
                      ) {
                        return null
                      }

                      // Don't render subgroups that don't have at least one active field in view mode
                      if (
                        mode === 'view' &&
                        subgroup.fields.every((field) => !activeFieldKeys.includes(field.key))
                      ) {
                        return null
                      }
                      return (
                        <FieldsSubgroup
                          key={subgroup.fields.map((field) => field.key).join(',')}
                          title={subgroup.title}
                          titleIcon={subgroup.titleIcon}
                          description={subgroup.description}
                          mode={mode}
                        >
                          <FormFields
                            form={form}
                            fields={subgroup.fields}
                            fieldsMap={fieldsMap}
                            setHistoryValues={setHistoryValues}
                            categoryMetadata={getCategoryMetadata({
                              category: document.category,
                              subcategories,
                            })}
                            mode={mode}
                            toggleFieldActive={toggleFieldActive}
                            activeFieldKeys={activeFieldKeys}
                          />
                        </FieldsSubgroup>
                      )
                    })}
                  </FieldsGroup>
                )
              })}
            </div>
          )}
          {mode === 'view' ? (
            <form.Subscribe
              selector={(state) => {
                return {
                  isDirty: state.isDirty,
                  isSubmitting: state.isSubmitting,
                  isValid: state.isValid,
                  values: state.values,
                  isDefaultValue: state.isDefaultValue,
                }
              }}
              children={({ isDirty, isSubmitting, isValid, values, isDefaultValue }) => {
                return (
                  <ReviewActions
                    isDirty={isDirty}
                    isSubmitDisabled={
                      stepFlag.hasActiveStepFlag
                        ? false
                        : isSubmitting ||
                          !isValid ||
                          document.review_status === DOCUMENT_SERVER_STATUS.processing ||
                          !tagsReady ||
                          (!wasReclassified &&
                            getIsDataExtractionFinished(document, subcategories) &&
                            isDefaultValue)
                    }
                    onSubmit={
                      stepFlag.hasActiveStepFlag
                        ? () => navigate(getDocumentsUrlWithCurrentParams())
                        : form.handleSubmit
                    }
                    submitLabel={
                      stepFlag.hasActiveStepFlag
                        ? 'Return to Work Queue'
                        : noFieldsPresent
                          ? 'Finish'
                          : 'Validate'
                    }
                    submitTooltip={
                      stepFlag.hasActiveStepFlag
                        ? undefined
                        : !tagsReady
                          ? 'opti is processing this document. Please wait for it to complete.'
                          : getShouldShowMandatoryComment(
                                values.carc_codes,
                                values.rarc_codes,
                                values.natera_tags,
                              ) && !values.natera_tags_notes?.trim()
                            ? 'Enter a brief explanation to validate.'
                            : !isValid
                              ? 'Fill all the required fields.'
                              : undefined
                    }
                    canUndo={canUndo}
                    canRedo={canRedo}
                    onRevert={() => {
                      form.reset()
                      resetHistoryValues()
                    }}
                    onUndo={undo}
                    onRedo={redo}
                    previousStepUrl={getDocumentStepUrl(document.key, 'classification', {
                      queryParams,
                    })}
                    previousStepLabel="Go to classification"
                    isError={isDenialDataReviewError || isCarcRarcDataReviewError}
                    errorContent="opti ran into a problem while confirming the extracted data. Please try again."
                    isScrolled={isScrolled}
                  />
                )
              }}
            />
          ) : null}
          {sentinelElement}
        </form>
      </LayoutGroup>
    )
  },
)
